import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n/hooks.ts";
import type { SubBasin } from "../model/types.ts";
import { SearchIcon } from "./SearchIcon.tsx";

const MAX_RESULTS = 20;

/**
 * "Find a sub-basin by name" — always available in TopBar.tsx, since with a
 * real large project (Duero: 1728 sub-basins) hunting for one visually on
 * the map/cards isn't realistic. Deliberately just a locator, not a filter:
 * picking a match switches to map view if needed and pans/highlights it
 * (SubBasinMap.tsx's `highlightedSubBasinCode` + PanToSubBasin.tsx) — it
 * never opens the edit dialog on its own, that's still a second, deliberate
 * click on the map once you can see where it is.
 *
 * Controlled open/onToggle/onClose (same pattern as SettingsMenu.tsx) so
 * TopBar.tsx's single `popover` state closes this the same way as every
 * other menu — including its own outside-click handling, already wired
 * once at the bar level.
 */
export function SubBasinSearch({
  subBasins,
  open,
  onToggle,
  onClose,
  onSelect,
}: {
  subBasins: SubBasin[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelect: (subBasinCode: number) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      inputRef.current?.focus();
    }
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return subBasins.filter((s) => s.description.toLowerCase().includes(q)).slice(0, MAX_RESULTS);
  }, [subBasins, query]);

  function pick(subBasinCode: number) {
    onSelect(subBasinCode);
    onClose();
  }

  return (
    <div style={{ position: "relative" }}>
      <button
        className="button-icon"
        onClick={onToggle}
        aria-label={t("topbar.searchSubBasin")}
        title={t("topbar.searchSubBasin")}
      >
        <SearchIcon />
      </button>
      {open && (
        <div className="popover subbasin-search-popover">
          <input
            ref={inputRef}
            type="text"
            className="subbasin-search-input"
            placeholder={t("topbar.searchSubBasinPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches.length > 0) pick(matches[0].subBasinCode);
              else if (e.key === "Escape") onClose();
            }}
          />
          {query.trim() !== "" && (
            <ul className="subbasin-search-results">
              {matches.length === 0 ? (
                <li className="subbasin-search-empty">{t("topbar.searchSubBasinNoMatches")}</li>
              ) : (
                matches.map((s) => (
                  <li key={s.subBasinCode}>
                    <button className="button" onClick={() => pick(s.subBasinCode)}>
                      {s.description}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
